import { useEffect, useState, useRef } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip,
} from "recharts";
import { toast } from "sonner";
import { Loader2, LogOut, TrendingUp, Gauge, Lock, ShieldCheck, FileSignature, Download, Check, Eraser } from "lucide-react";

const scoreColor = (s) => (s >= 70 ? "#00A859" : s >= 40 ? "#F59E0B" : "#FF2A2A");
const fmtEur = (v) => `€ ${String(Math.round(Number(v || 0))).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
const fmtDateTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
};

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const ink = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };
  const start = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#030712";
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!ink.current) { ink.current = true; setHasInk(true); }
  };
  const end = () => {
    if (drawing.current && ink.current) onChange(canvasRef.current.toDataURL("image/png"));
    drawing.current = false;
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    ink.current = false; setHasInk(false); onChange(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef} width={380} height={130} data-testid="signature-canvas"
        className="border border-dashed border-border bg-white w-full touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="flex items-center justify-between mt-1">
        <span className="text-xs text-muted-foreground">{hasInk ? "Firma acquisita" : "Disegna la firma qui sopra (opzionale)"}</span>
        {hasInk && (
          <button type="button" onClick={clear} data-testid="signature-clear-btn" className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1">
            <Eraser className="w-3 h-3" /> Cancella
          </button>
        )}
      </div>
    </div>
  );
}

function AcceptProposalDialog({ onAccepted }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/portal/proposal/accept", { signer_name: name, signature });
      toast.success("Proposta accettata e firmata");
      setOpen(false);
      onAccepted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-none" data-testid="accept-proposal-btn">
          <FileSignature className="w-4 h-4 mr-2" /> Accetta e firma
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading font-black tracking-tight">Accetta e firma la proposta</DialogTitle>
          <DialogDescription>Inserisci il nome del firmatario e conferma l'accettazione della proposta commerciale.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="data-label">Nome e cognome del firmatario</Label>
            <Input data-testid="signer-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 rounded-none" placeholder="Mario Rossi" />
          </div>
          <div>
            <Label className="data-label">Firma disegnata (opzionale)</Label>
            <div className="mt-1"><SignaturePad onChange={setSignature} /></div>
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" data-testid="accept-terms-checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5" required />
            <span>Dichiaro di aver letto la proposta commerciale e di accettarne i contenuti. Data, ora e indirizzo IP verranno registrati come prova dell'accettazione.</span>
          </label>
          <DialogFooter>
            <Button type="submit" data-testid="accept-proposal-submit-btn" disabled={busy || !agreed || !name.trim()} className="rounded-none w-full">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Conferma accettazione
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ProposalSection() {
  const [prop, setProp] = useState(null);
  const [payCfg, setPayCfg] = useState(null);
  const [paying, setPaying] = useState(false);

  const load = () => api.get("/portal/proposal").then((r) => setProp(r.data)).catch(() => setProp(null));

  useEffect(() => {
    api.get("/payments/config").then((r) => setPayCfg(r.data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (params.get("paypal") === "success" && token) {
      window.history.replaceState({}, "", "/portal");
      api.post("/payments/proposal/capture", { order_id: token })
        .then(() => toast.success("Pagamento ricevuto, grazie!"))
        .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Errore nella conferma del pagamento"))
        .finally(load);
    } else {
      if (params.get("paypal") === "cancel") {
        window.history.replaceState({}, "", "/portal");
        toast.info("Pagamento annullato");
      }
      load();
    }
  }, []);

  const payWithPaypal = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/proposal/checkout");
      window.location.href = data.approve_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore PayPal");
      setPaying(false);
    }
  };

  if (!prop?.available) return null;
  const acc = prop.acceptance;

  return (
    <div className="bg-white border border-border p-6" data-testid="portal-proposal">
      <div className="flex items-center gap-2 data-label mb-4"><FileSignature className="w-3.5 h-3.5 text-primary" /> Proposta commerciale</div>
      <div className="flex flex-col md:flex-row md:items-start gap-6">
        <div className="flex-1">
          <div className="font-heading font-bold text-lg tracking-tight" data-testid="proposal-offer-name">{prop.offer?.name}</div>
          <ul className="mt-3 space-y-1.5 text-sm">
            {(prop.offer?.includes || []).map((x, i) => (
              <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {x}</li>
            ))}
          </ul>
        </div>
        <div className="md:w-64 shrink-0 space-y-3">
          <div className="border border-border p-4">
            <div className="data-label">Investimento stimato</div>
            <div className="font-mono font-semibold text-lg mt-1" data-testid="proposal-investment">
              {fmtEur(prop.investment?.min)} – {fmtEur(prop.investment?.max)}
            </div>
          </div>
          <Button variant="outline" className="rounded-none w-full" data-testid="portal-download-proposal-btn" onClick={() => window.open(`${API}/portal/proposal.pdf`, "_blank")}>
            <Download className="w-4 h-4 mr-2" /> Scarica PDF
          </Button>
          {acc ? (
            <div className="border border-emerald-300 bg-emerald-50 p-4" data-testid="proposal-accepted-box">
              <div className="flex items-center gap-1.5 text-emerald-800 font-bold text-sm"><Check className="w-4 h-4" /> Proposta accettata</div>
              <p className="text-xs text-emerald-800 mt-1">Firmata da <b>{acc.signer_name}</b><br />il {fmtDateTime(acc.accepted_at)}</p>
              {acc.paid ? (
                <p className="text-xs text-emerald-800 mt-2 font-bold flex items-center gap-1" data-testid="proposal-paid-box">
                  <Check className="w-3.5 h-3.5" /> Acconto pagato ({fmtEur(acc.paid_amount)})
                </p>
              ) : payCfg?.configured ? (
                <Button data-testid="pay-proposal-btn" onClick={payWithPaypal} disabled={paying} className="rounded-none w-full mt-3 bg-[#0070BA] hover:bg-[#005ea6] text-white">
                  {paying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Paga acconto con PayPal ({fmtEur(prop.investment?.min)})
                </Button>
              ) : null}
            </div>
          ) : (
            <AcceptProposalDialog onAccepted={load} />
          )}
        </div>
      </div>
    </div>
  );
}

export default function ClientPortal() {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);

  const loadData = async () => {
    const { data } = await api.get("/portal/data");
    setData(data);
  };

  const check = async () => {
    try {
      await api.get("/portal/me");
      setAuthed(true);
      await loadData();
    } catch {
      setAuthed(false);
    } finally {
      setChecking(false);
    }
  };
  useEffect(() => { check(); }, []);

  const logout = async () => {
    await api.post("/portal/logout");
    setAuthed(false); setData(null);
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6]"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  if (!authed) return <PortalLogin onSuccess={() => { setChecking(true); check(); }} />;

  return <PortalDashboard data={data} onLogout={logout} />;
}

function PortalLogin({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/portal/login", { email, password });
      toast.success("Accesso effettuato");
      onSuccess();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#030712] p-4">
      <div className="w-full max-w-md bg-white border border-border" data-testid="portal-login">
        <div className="bg-primary text-primary-foreground px-8 py-6">
          <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5" /><span className="font-heading font-bold tracking-tight text-lg">Portale Cliente</span></div>
          <p className="text-xs opacity-80 mt-1">Accedi per consultare i tuoi risultati e KPI</p>
        </div>
        <form onSubmit={submit} className="p-8 space-y-4">
          <div>
            <Label className="data-label">Email</Label>
            <Input data-testid="portal-email-input" type="email" className="mt-1 rounded-none" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <Label className="data-label">Password</Label>
            <Input data-testid="portal-password-input" type="password" className="mt-1 rounded-none" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <Button type="submit" data-testid="portal-login-btn" className="w-full rounded-none" disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />} Accedi
          </Button>
          <p className="text-xs text-muted-foreground text-center">Credenziali fornite dal tuo consulente.</p>
        </form>
      </div>
    </div>
  );
}

function PortalDashboard({ data, onLogout }) {
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  const trend = (data.score_trend || []).map((t) => ({
    d: t.date ? new Date(t.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" }) : "",
    v: t.overall,
  }));
  const tier = data.latest_tier || {};

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <header className="bg-white border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            <div>
              <div className="font-heading font-bold tracking-tight" data-testid="portal-client-name">{data.client?.name}</div>
              <div className="data-label">{data.client?.sector || "Portale cliente"}</div>
            </div>
          </div>
          <Button variant="outline" size="sm" className="rounded-none" onClick={onLogout} data-testid="portal-logout-btn"><LogOut className="w-4 h-4 mr-2" /> Esci</Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white border border-border p-6">
            <div className="data-label">Punteggio attuale</div>
            <div className="font-mono font-medium text-6xl mt-2" style={{ color: scoreColor(data.latest_overall ?? 0) }} data-testid="portal-overall">
              {data.latest_overall ?? "—"}
            </div>
            <div className="text-xs text-muted-foreground">/ 100</div>
            {tier.label && <span className="inline-block mt-3 px-3 py-1 text-xs font-bold tracking-wide text-white" style={{ backgroundColor: tier.color }}>{tier.label}</span>}
          </div>

          <div className="lg:col-span-2 bg-white border border-border p-6" data-testid="portal-trend">
            <div className="flex items-center gap-2 mb-4"><TrendingUp className="w-4 h-4 text-primary" /><h3 className="font-heading font-bold text-lg tracking-tight">Evoluzione punteggio</h3></div>
            {trend.length >= 2 ? (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                  <XAxis dataKey="d" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#9CA3AF" />
                  <RTooltip contentStyle={{ borderRadius: 0, border: "1px solid #E5E7EB", fontSize: 12 }} />
                  <Line type="monotone" dataKey="v" stroke="#002FA7" strokeWidth={2} dot={{ r: 4, fill: "#002FA7" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">Il grafico di evoluzione comparirà dopo il secondo assessment.</p>
            )}
          </div>
        </div>

        <ProposalSection />

        <div className="bg-white border border-border p-6" data-testid="portal-kpis">
          <div className="flex items-center gap-2 data-label mb-4"><Gauge className="w-3.5 h-3.5 text-primary" /> I tuoi KPI</div>
          {(data.kpis || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessun KPI ancora disponibile. Il tuo consulente li aggiornerà a breve.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {data.kpis.map((k, i) => {
                const pos = (k.improvement ?? 0) >= 0;
                return (
                  <div key={i} className="border border-border p-4" data-testid={`portal-kpi-${i}`}>
                    <div className="font-medium text-sm">{k.name}</div>
                    <div className="data-label">{k.unit}</div>
                    <div className="flex items-end gap-3 mt-3">
                      <div><div className="data-label">Baseline</div><div className="font-mono text-lg">{k.baseline ?? 0}</div></div>
                      <div className="text-muted-foreground pb-1">→</div>
                      <div><div className="data-label">Attuale</div><div className="font-mono text-lg font-semibold">{k.current ?? 0}</div></div>
                      <div className="ml-auto text-right">
                        <div className={`font-mono font-bold ${pos ? "text-[#00A859]" : "text-destructive"}`}>{pos ? "+" : ""}{k.improvement ?? 0}</div>
                        {k.improvement_pct != null && <div className={`text-xs ${pos ? "text-[#00A859]" : "text-destructive"}`}>{pos ? "+" : ""}{k.improvement_pct}%</div>}
                      </div>
                    </div>
                    {k.target != null && <div className="text-xs text-muted-foreground mt-1">Target: <span className="font-mono">{k.target} {k.unit}</span></div>}
                    {(k.readings || []).length >= 2 && (
                      <ResponsiveContainer width="100%" height={70}>
                        <LineChart data={k.readings.map((r) => ({ d: r.date ? new Date(r.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }) : "", v: r.value }))} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
                          <XAxis dataKey="d" tick={{ fontSize: 9 }} stroke="#9CA3AF" />
                          <YAxis tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} stroke="#9CA3AF" width={30} />
                          <Line type="monotone" dataKey="v" stroke="#002FA7" strokeWidth={2} dot={{ r: 2 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
